"use client";

import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { ChevronDown, LogOut } from "lucide-react";

import { useAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/button";
import {
  getAccessiblePortals,
  isPortal,
  PREFERRED_PORTAL_KEY,
  Portal,
  PORTAL_INFO,
  setPreferredPortal,
} from "@/lib/portals";

function initials(firstName?: string, lastName?: string) {
  return `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase() || "?";
}

export default function SelectPortalPage() {
  const user = useAuth();
  const router = useRouter();
  const [ready, setReady] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const portals = useMemo(() => getAccessiblePortals(user), [user]);
  const firstName = user?.first_name?.trim() || "there";
  const fullName = user ? `${user.first_name} ${user.last_name}`.trim() : "Signed in";

  useEffect(() => {
    if (!user) return;
    const preferredPortal = window.localStorage.getItem(PREFERRED_PORTAL_KEY);
    if (isPortal(preferredPortal) && portals.includes(preferredPortal)) {
      router.replace(PORTAL_INFO[preferredPortal].href);
      return;
    }
    if (portals.length === 1) {
      router.replace(PORTAL_INFO[portals[0]].href);
      return;
    }
    setReady(true);
  }, [portals, router, user]);

  useEffect(() => {
    if (!profileOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setProfileOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [profileOpen]);

  const choose = (portal: Portal) => {
    setPreferredPortal(portal);
    router.push(PORTAL_INFO[portal].href);
  };

  if (!ready) return null;

  if (portals.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
        <div className="w-full max-w-md text-center">
          <h1 className="font-[family-name:var(--font-instrument-serif)] text-4xl tracking-[-0.025em]">
            No portal access
          </h1>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            Your account isn&apos;t assigned to an SKNV portal yet. Contact your administrator to
            request access.
          </p>
          <Button className="mt-8" onClick={() => router.push("/logout")}>
            <LogOut className="h-4 w-4" />
            Log out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas text-[#1c1411] dark:text-foreground">
      <header className="relative z-40 flex h-[82px] items-center justify-between border-b border-[#eee6da] px-6 sm:px-10 dark:border-border">
        <Image
          src="/sknv-logo.png"
          alt="SKNV"
          width={120}
          height={45}
          priority
          className="h-auto w-[120px] dark:brightness-0 dark:invert"
        />
        <div className="relative">
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={profileOpen}
            onClick={() => setProfileOpen((open) => !open)}
            className="flex cursor-pointer items-center gap-2.5 rounded-lg px-1.5 py-1 transition-colors hover:bg-[#f3ebe1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9d5d50] dark:hover:bg-accent"
          >
            <span
              className="flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold text-white"
              style={{ backgroundColor: "#9d5d50" }}
            >
              {initials(user?.first_name, user?.last_name)}
            </span>
            <span className="hidden min-w-0 text-left sm:block">
              <span className="block max-w-40 truncate text-[12px] font-semibold leading-[15px]">
                {fullName}
              </span>
              <span className="block text-[11px] leading-[14px] text-[#9c8d84] dark:text-muted-foreground">
                Signed in
              </span>
            </span>
            <ChevronDown
              className="hidden h-3 w-3 text-[#9c8d84] transition-transform sm:block"
              style={{ transform: profileOpen ? "rotate(180deg)" : undefined }}
            />
          </button>

          {profileOpen && (
            <>
              <button
                type="button"
                aria-label="Close account menu"
                onClick={() => setProfileOpen(false)}
                className="fixed inset-0 z-40 cursor-default"
              />
              <div
                role="menu"
                aria-label="Account menu"
                className="absolute right-0 top-full z-50 mt-2 w-[180px] rounded-[10px] border border-[#eee6da] bg-white p-1.5 shadow-[0_4px_10px_rgba(46,30,22,0.06),0_18px_44px_rgba(46,30,22,0.12)] dark:border-border dark:bg-popover"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => router.push("/logout")}
                  className="flex h-10 w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-left text-[12.5px] font-medium text-[#673d36] transition-colors hover:bg-[#f7f1ea] dark:text-sidebar-primary dark:hover:bg-accent"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1228px] px-6 pb-16 pt-14 sm:px-10">
        <div className="mb-9">
          <div>
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#786259] dark:text-muted-foreground">
              SKNV Platform · Portal hub
            </p>
            <h1 className="font-[family-name:var(--font-instrument-serif)] text-[42px] leading-[1.05] tracking-[-0.035em] sm:text-[48px]">
              Welcome back, {firstName}.
            </h1>
            <p className="mt-2.5 text-[15px] text-[#755f55] dark:text-muted-foreground">
              You have access to{" "}
              <strong className="font-semibold text-[#1c1411] dark:text-foreground">
                {portals.length} {portals.length === 1 ? "portal" : "portals"}
              </strong>
              .
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {portals.map((portal) => {
            const info = PORTAL_INFO[portal];
            const Icon = info.icon;
            return (
              <button
                key={portal}
                type="button"
                onClick={() => choose(portal)}
                className="group relative flex cursor-pointer flex-col items-center overflow-hidden rounded-[10px] border border-[#e8dfd3] bg-white px-4 py-6 text-center shadow-[0_2px_5px_rgba(46,30,22,0.08)] transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-[0_8px_22px_rgba(46,30,22,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9d5d50] focus-visible:ring-offset-2 focus-visible:ring-offset-canvas dark:border-border dark:bg-card"
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 top-0 h-[3px]"
                  style={{ backgroundColor: info.accent }}
                />

                <span
                  aria-hidden="true"
                  className="flex h-11 w-11 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${info.accent}1a` }}
                >
                  <Icon className="h-5 w-5" style={{ color: info.accent }} />
                </span>
                <span className="mt-3 font-[family-name:var(--font-instrument-serif)] text-[19px] leading-[1.15] tracking-[-0.02em]">
                  {info.label}
                </span>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
